<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/icon'
import { computed, type HTMLAttributes } from 'vue'
import { cn } from '@/lib/utils'

interface Props {
  title?: string
  description?: string
  /**
   * Keeps the title as the dialog's accessible name but drops it from view, for
   * modals whose content already carries a heading of its own.
   */
  hideTitle?: boolean
  showCloseButton?: boolean
  closeButtonColor?: string
  open?: boolean
  class?: HTMLAttributes['class']
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean // Defaults to the same behavior as closeOnBackdropClick
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full'
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  description: '',
  hideTitle: false,
  showCloseButton: true,
  closeButtonColor: 'secondary-2-500',
  open: false,
  class: '',
  closeOnBackdropClick: true,
  closeOnEscape: undefined,
  size: 'md'
})

const emit = defineEmits(['close'])

const escapeCloses = computed(() => props.closeOnEscape ?? props.closeOnBackdropClick)

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-[95vw]'
}
</script>

<template>
  <Dialog :open="props.open" @update:open="(v) => !v && emit('close')">
    <DialogContent
      :class="
        cn(
          'bg-base-background shadow-md max-h-[90vh] flex flex-col p-0 w-[95vw]',
          sizeClasses[props.size],
          props.class
        )
      "
      @pointer-down-outside="
        (event: Event) => {
          if (props.closeOnBackdropClick) {
            emit('close')
          } else {
            event.preventDefault()
          }
        }
      "
      @escape-key-down="
        (event: KeyboardEvent) => {
          if (escapeCloses) {
            emit('close')
          } else {
            event.preventDefault()
          }
        }
      "
    >
      <slot name="image" />
      <DialogHeader
        :class="[
          'flex text-start justify-between items-start px-6 shrink-0',
          props.hideTitle ? 'pt-4' : 'pt-6'
        ]"
      >
        <div class="max-w-full">
          <DialogTitle
            v-if="props.title"
            :class="
              props.hideTitle
                ? 'sr-only'
                : cn(
                    'text-gray-warm-900 text-lg',
                    $slots['title-suffix'] && 'flex flex-wrap items-center gap-x-2 gap-y-1'
                  )
            "
          >
            {{ props.title }}
            <slot name="title-suffix" />
          </DialogTitle>
          <DialogDescription
            v-if="props.description"
            :class="props.hideTitle ? 'sr-only' : 'text-gray-warm-600 text-sm mt-2'"
          >
            {{ props.description }}
          </DialogDescription>
        </div>
        <Button
          v-if="props.showCloseButton"
          hierarchy="link-color"
          class="absolute top-3 right-3 z-20 cursor-pointer"
          @click="emit('close')"
        >
          <Icon name="x" :stroke-color="props.closeButtonColor" size="md" />
        </Button>
      </DialogHeader>

      <div class="px-6 overflow-y-auto">
        <slot></slot>
      </div>

      <DialogFooter class="shrink-0 sm:justify-center pb-4 px-6 mt-auto">
        <slot name="footer"></slot>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
